import { useCallback, useEffect, useRef } from 'react';
const CARD_PICKER_PAGE_SIZE = 500;
const FILTER_DEBOUNCE_MS = 350;
const EMPTY_BASE_QUERY = {};
const HOTLISTED_CARD_STATUS = 'HOTLISTED';
/** Only list cards eligible for replacement / freeze requests. */
export const CARD_PICKER_ACTIVE_STATUS_QUERY = {
    'status.equals': 'ACTIVE',
};
export const toCardPickerOption = (c) => {
    if (c.id == null || c.id <= 0) {
        return null;
    }
    return {
        id: c.id,
        label: `${c.cardReference ?? '—'} — ${c.maskedPan ?? ''}`,
    };
};
const appendStatusNotEqualsHotlisted = (baseQuery) => {
    const key = 'status.notEquals';
    const existing = baseQuery[key];
    if (existing == null || existing === '') {
        return { ...baseQuery, [key]: HOTLISTED_CARD_STATUS };
    }
    if (Array.isArray(existing)) {
        if (existing.some(v => String(v).toUpperCase() === HOTLISTED_CARD_STATUS)) {
            return baseQuery;
        }
        return { ...baseQuery, [key]: [...existing, HOTLISTED_CARD_STATUS] };
    }
    if (String(existing).toUpperCase() === HOTLISTED_CARD_STATUS) {
        return baseQuery;
    }
    return { ...baseQuery, [key]: [existing, HOTLISTED_CARD_STATUS] };
};
const buildCardPickerQuery = (filter, baseQuery = {}) => {
    const params = {
        ...appendStatusNotEqualsHotlisted(baseQuery),
        page: 0,
        size: CARD_PICKER_PAGE_SIZE,
        sort: 'id,desc',
    };
    const term = filter.trim();
    if (term) {
        params['cardReference.contains'] = term;
    }
    else {
        delete params['cardReference.contains'];
    }
    return params;
};
/** Server-side card search for dropdowns (`cardReference.contains`). */
export function useCardPickerSearch(active, loadCards, baseQuery = EMPTY_BASE_QUERY) {
    const filterDebounceRef = useRef();
    const loadCardsRef = useRef(loadCards);
    const baseQueryRef = useRef(baseQuery);
    loadCardsRef.current = loadCards;
    baseQueryRef.current = baseQuery;
    const fetchCards = useCallback((filter) => {
        loadCardsRef.current(buildCardPickerQuery(filter, baseQueryRef.current));
    }, []);
    const baseQueryKey = JSON.stringify(baseQuery);
    useEffect(() => {
        if (!active) {
            return;
        }
        fetchCards('');
    }, [active, fetchCards, baseQueryKey]);
    useEffect(() => () => {
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }
    }, []);
    const onFilter = useCallback((event) => {
        const next = event.filter ?? '';
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }
        filterDebounceRef.current = setTimeout(() => {
            fetchCards(next);
        }, FILTER_DEBOUNCE_MS);
    }, [fetchCards]);
    return { onFilter };
}
